/* ============================================
   Cart upsell — swap only.

   Behaviour mode (REMOVE_ON_SWAP):
     true  -> remove the original 1-pack when adding the 3-pack
     false -> leave the original 1-pack in the cart, just add the 3-pack
   ============================================ */


const REMOVE_ON_SWAP = true;

class CartUpsell extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('[data-cart-upsell-swap]');
    this.onePackVariantId = this.dataset.onePackVariantId;
    this.threePackVariantId = this.dataset.threePackVariantId;
    this.threePackPlanId = this.dataset.threePackPlanId || null;
    this.currentLine = parseInt(this.dataset.currentLine, 10);

    if (this.button) {
      this.button.addEventListener('click', this.swap.bind(this));
    }
  }

  getCartParent() {
    return (
      this.closest('cart-items') ||
      this.closest('cart-drawer-items') ||
      document.querySelector('cart-items') ||
      document.querySelector('cart-drawer-items')
    );
  }

resetButton() {
  if (this.button) {
    this.button.disabled = false;
    this.button.classList.remove('is-loading');
    if (this._originalButtonText) {
      this.button.textContent = this._originalButtonText;
    }
  }
}

  async swap() {
    // One-shot lock: ignore additional clicks while a swap is in progress.
    if (this._swapInFlight) return;
    this._swapInFlight = true;

    const cartParent = this.getCartParent();
    if (!cartParent || !this.threePackVariantId) {
      this._swapInFlight = false;
      return;
    }

    this.button.disabled = true;
    this.button.classList.add('is-loading');
    this._originalButtonText = this.button.textContent;
    this.button.textContent = 'Adding...';

    const timeoutMs = 8000;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      controller.abort();
      this.resetButton();
    }, timeoutMs);

    try {
      const isSubscription = !!this.threePackPlanId;
      const isDrawer = !!this.closest('cart-drawer-items');
      const sections = isDrawer
        ? null
        : cartParent.getSectionsToRender().map((s) => s.section);

      // Step 1: remove the original 1-pack (if configured to do so).
      // Find the specific line by its variant ID — robust against cart reordering.
      if (REMOVE_ON_SWAP) {
        const cartRes = await fetch('/cart.js', {
          credentials: 'same-origin',
          signal: controller.signal,
        });
        const cart = await cartRes.json();
        const onePackId = parseInt(this.onePackVariantId, 10);
        const oneVariant = cart.items.find((item) => item.variant_id === onePackId);
        if (!oneVariant) throw new Error('1-pack line not found by variant id');

        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          signal: controller.signal,
          body: JSON.stringify({ id: oneVariant.key, quantity: 0 }),
        });
      }

      // Step 2: add the 3-pack (with selling plan if subscription).
      const addBody = {
        items: [{
          id: parseInt(this.threePackVariantId, 10),
          quantity: 1,
        }],
      };
      if (isSubscription) {
        addBody.items[0].selling_plan = parseInt(this.threePackPlanId, 10);
      }
      if (sections) {
        addBody.sections = sections;
        addBody.sections_url = window.location.pathname;
      }

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        signal: controller.signal,
        body: JSON.stringify(addBody),
      });
      const state = await addRes.json();

      clearTimeout(timer);

      // Re-render registered cart sections (cart page only; drawer refreshes itself).
      if (state && state.sections) {
        cartParent.getSectionsToRender().forEach((section) => {
          const target = document.getElementById(section.id);
          if (!target) return;
          const inner = target.querySelector(section.selector) || target;
          const sourceHtml = state.sections[section.section];
          if (!sourceHtml) return;
          const parsed = new DOMParser().parseFromString(sourceHtml, 'text/html');
          const sourceInner = parsed.querySelector(section.selector) || parsed.body;
          inner.innerHTML = sourceInner.innerHTML;
        });
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-upsell',
          cartData: state,
          variantId: this.threePackVariantId,
        });
      }
    } catch (e) {
      console.error('[cart-upsell] swap failed', e);
      clearTimeout(timer);
      this.resetButton();
    } finally {
      this._swapInFlight = false;
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}